"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection from "@/components/marketing/AnimatedSection";

type Cell = string | { en: string; es: string };

interface Row {
  feat: { en: string; es: string };
  sub: { en: string; es: string };
  doordash: Cell;
  redy: Cell;
  redyHighlight?: boolean;
}

const ROWS: Row[] = [
  {
    feat: { en: "Commission", es: "Comisión" },
    sub: { en: "Taken off the order value", es: "Sobre el valor del pedido" },
    doordash: { en: "~$11.25 (25%, example)", es: "~$11.25 (25%, ejemplo)" },
    redy: "$0",
    redyHighlight: true,
  },
  {
    feat: { en: "Platform fee", es: "Fee de plataforma" },
    sub: { en: "Per delivered delivery", es: "Por envío entregado" },
    doordash: "—",
    redy: { en: "from $2.99", es: "desde $2.99" },
    redyHighlight: true,
  },
  {
    feat: { en: "Menu prices", es: "Precios del menú" },
    sub: {
      en: "What the diner sees on the item",
      es: "Lo que el cliente ve en el plato",
    },
    doordash: { en: "Often raised to cover commission", es: "Muchas veces subidos para cubrir la comisión" },
    redy: { en: "Your real prices", es: "Tus precios reales" },
  },
  {
    feat: { en: "Who pays the courier", es: "Quién paga al courier" },
    sub: { en: "The actual cost of the trip", es: "El costo real del viaje" },
    doordash: { en: "Varies by app", es: "Depende de la app" },
    redy: { en: "Diner — live Uber Direct quote", es: "Cliente — cotización en vivo de Uber Direct" },
  },
  {
    feat: { en: "Cancelled delivery", es: "Envío cancelado" },
    sub: { en: "Food never got there", es: "La comida no llegó" },
    doordash: { en: "Varies by app", es: "Depende de la app" },
    redy: { en: "Redy charges $0", es: "Redy te cobra $0" },
    redyHighlight: true,
  },
  {
    feat: { en: "Customer + their data", es: "El cliente y sus datos" },
    sub: { en: "Phone, email, order history", es: "Teléfono, email, historial" },
    doordash: { en: "Depends on the app", es: "Depende de la app" },
    redy: { en: "Yours", es: "Tuyos" },
  },
  {
    feat: { en: "Whose brand they see", es: "Qué marca ven" },
    sub: { en: "Checkout and tracking screen", es: "Checkout y pantalla de seguimiento" },
    doordash: { en: "The app's", es: "La de la app" },
    redy: { en: "Yours", es: "Tuya" },
  },
  {
    feat: { en: "Who brings new customers", es: "Quién trae clientes nuevos" },
    sub: { en: "Discovery — being found", es: "Descubrimiento — que te encuentren" },
    doordash: { en: "The app (genuinely)", es: "La app (de verdad)" },
    redy: { en: "You do", es: "Tú" },
  },
];

export default function DeliveryCompareSection() {
  const { t } = useLang();

  const renderCell = (val: Cell, isRedy = false, highlight = false) => {
    const text = typeof val === "string" ? val : t(val.en, val.es);
    const colorClass = isRedy
      ? highlight
        ? "text-redy-red"
        : "text-redy-charcoal"
      : "text-redy-muted font-normal";

    return (
      <div
        className={`col-span-1 text-center flex items-center justify-center text-[14px] font-bold ${colorClass}`}
      >
        {text}
      </div>
    );
  };

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[1080px] mx-auto">
        <AnimatedSection className="text-center">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-red mb-4">
            {t("One $45 order, side by side (example)", "Un pedido de $45, lado a lado (ejemplo)")}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] text-redy-charcoal">
            {t("Same food. Same customer.", "Misma comida. Mismo cliente.")}
            <br />
            <span className="text-redy-charcoal/30">
              {t("$8.26 of difference.", "$8.26 de diferencia.")}
            </span>
          </h2>
          <p className="text-[15px] font-medium text-redy-secondary max-w-2xl mx-auto mt-4">
            {t(
              "A regular you already had places a $45 delivery order. Here's where the money ends up, line by line.",
              "Un cliente que ya tenías hace un pedido de $45 a domicilio. Aquí termina el dinero, línea por línea."
            )}
          </p>
        </AnimatedSection>

        <AnimatedSection
          delay={0.2}
          className="mt-16 bg-white border border-black/5 rounded-3xl overflow-hidden shadow-sm"
        >
          {/* Mobile scroll hint */}
          <div className="md:hidden text-center text-[11px] font-bold text-redy-secondary/60 uppercase tracking-wider py-2 bg-[#FAFAFA] border-b border-black/5">
            {t("← Scroll to compare →", "← Desliza para comparar →")}
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[640px]">
              {/* Header */}
              <div className="grid grid-cols-3 bg-redy-charcoal px-6 py-5">
                <div className="col-span-1 text-[13px] font-bold text-white/50">
                  {t("On a $45 order", "En un pedido de $45")}
                </div>
                <div className="col-span-1 text-[13px] font-bold text-white/50 text-center">
                  {t("Marketplace app", "App de marketplace")}
                </div>
                <div className="col-span-1 text-[13px] font-bold text-white text-center">Redy</div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-black/5">
                {ROWS.map((row, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-3 px-6 py-5 hover:bg-black/[0.02] transition-colors"
                  >
                    <div className="col-span-1">
                      <div className="text-[14px] font-bold text-redy-charcoal">
                        {t(row.feat.en, row.feat.es)}
                      </div>
                      <div className="text-[12px] text-redy-secondary mt-0.5">
                        {t(row.sub.en, row.sub.es)}
                      </div>
                    </div>
                    {renderCell(row.doordash, false, false)}
                    {renderCell(row.redy, true, row.redyHighlight)}
                  </div>
                ))}
              </div>

              {/* Bottom line */}
              <div className="grid grid-cols-3 px-6 py-7 bg-[#FAFAF8] border-t border-black/5">
                <div className="col-span-1">
                  <div className="text-[15px] font-black text-redy-charcoal">
                    {t("Restaurant keeps", "Al restaurante le queda")}
                  </div>
                  <div className="text-[12px] text-redy-secondary mt-0.5">
                    {t("Out of the $45 order value", "De los $45 del pedido")}
                  </div>
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  <span className="text-[26px] md:text-[30px] font-black tabular-nums text-redy-charcoal/40">
                    ~$33.75
                  </span>
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  <span
                    className="text-[26px] md:text-[30px] font-black tabular-nums"
                    style={{ color: "#D7263D" }}
                  >
                    ~$42.01
                  </span>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.35} className="mt-10 max-w-[720px] mx-auto">
          <p className="text-center text-[14px] font-medium text-redy-secondary leading-relaxed">
            {t(
              "In this example, $8.26 more per order. At 120 delivery orders a month that's $991 — about $11,900 a year, on orders you were already getting.",
              "En este ejemplo, $8.26 más por pedido. Con 120 pedidos a domicilio al mes son $991 — unos $11,900 al año, sobre pedidos que ya recibías."
            )}
          </p>
          <p className="text-center text-[12px] font-medium text-redy-secondary/60 mt-4 leading-relaxed">
            {t(
              "The example assumes a 25% marketplace commission; real rates vary by app and plan. Redy's fee (from $2.99, by plan) is charged only on deliveries that actually get delivered.",
              "El ejemplo asume 25% de comisión de marketplace; la tasa real varía según la app y el plan. La tarifa de Redy (desde $2.99, según tu plan) se cobra solo sobre envíos que sí se entregan."
            )}
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
}
